/**
 * Datasources Management Page
 * Every system, one layer: coverage at the top, then one connector card per
 * datasource with its freshness, throughput and sync settings.
 */

import React, { useState, useEffect } from 'react';
import { datasourceService } from '../../services/datasources.service';
import { Datasource } from '../../types/platform.types';
import { AddDatasourceModal } from '../../components/datasources/AddDatasourceModal';
import { DatasourceDetailModal } from '../../components/datasources/DatasourceDetailModal';
import { DatasourceCard } from '../../components/datasources/DatasourceCard';
import { AppShell } from '../../components/shell/AppShell';
import { Icon, ICONS } from '../../components/shell/icons';
import { DocumentUpload } from '../../components/datasources/DocumentUpload';

type SortOption = 'name' | 'type' | 'created' | 'lastSync';
type FilterType = string;

const TYPE_LABELS: Record<string, string> = {
  salesforce: 'Salesforce',
  sap_erp: 'SAP ERP',
  odoo: 'Odoo',
  dynamics365: 'Dynamics 365',
  oracle_erp: 'Oracle ERP',
  workday: 'Workday',
  postgresql: 'PostgreSQL',
  mysql: 'MySQL',
  mssql: 'SQL Server',
  rest_api: 'REST API',
  custom: 'Custom',
};

function Kpi({
  label,
  value,
  unit,
  color,
  icon,
  sub,
}: {
  label: string;
  value: string | number;
  unit?: string;
  color: string;
  icon: React.ReactNode;
  sub?: string;
}) {
  return (
    <div className="kpi">
      <div className="kpi-top">
        <span
          className="kic"
          style={{ background: `color-mix(in srgb, ${color} 13%, #fff)`, color }}
        >
          <Icon d={icon} />
        </span>
      </div>
      <div className="v">
        {value}
        {unit && <small>{unit}</small>}
      </div>
      <div className="k">{label}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

export const DatasourcesPage: React.FC = () => {
  const [datasources, setDatasources] = useState<Datasource[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDatasource, setSelectedDatasource] = useState<Datasource | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  // filter chips follow what is actually connected, not a fixed list
  const [sortBy, setSortBy] = useState<SortOption>('name');

  useEffect(() => {
    loadDatasources();
  }, []);

  const loadDatasources = async () => {
    try {
      setLoading(true);
      const data = await datasourceService.listDatasources();
      setDatasources(data);
    } catch (error) {
      console.error('Failed to load datasources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDatasourceCreated = () => {
    setShowAddModal(false);
    loadDatasources();
  };

  const handleDatasourceUpdated = () => {
    setSelectedDatasource(null);
    loadDatasources();
  };

  const handleDeleteDatasource = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this datasource?')) {
      return;
    }

    try {
      await datasourceService.deleteDatasource(id);
      loadDatasources();
    } catch (error) {
      console.error('Failed to delete datasource:', error);
      alert('Failed to delete datasource');
    }
  };

  // Filter datasources
  const filteredDatasources = datasources.filter((ds) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch =
        ds.name.toLowerCase().includes(query) ||
        ds.description?.toLowerCase().includes(query) ||
        ds.type.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    if (filterType !== 'all' && ds.type !== filterType) {
      return false;
    }

    return true;
  });

  // Sort datasources
  const sortedDatasources = [...filteredDatasources].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'type':
        return a.type.localeCompare(b.type);
      case 'created':
        return new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime();
      case 'lastSync':
        if (!a.last_sync_at && !b.last_sync_at) return 0;
        if (!a.last_sync_at) return 1;
        if (!b.last_sync_at) return -1;
        return new Date(b.last_sync_at).getTime() - new Date(a.last_sync_at).getTime();
      default:
        return 0;
    }
  });

  const presentTypes = Array.from(new Set(datasources.map((d) => d.type)))
    .sort()
    .map((t) => ({ id: t as string, label: TYPE_LABELS[t] || t }));

  const activeCount = datasources.filter((d) => d.status === 'active').length;
  const errorCount = datasources.filter((d) => d.status === 'error').length;
  const totalQueries = datasources.reduce((sum, d) => sum + (d.total_queries || 0), 0);
  const withTiming = datasources.filter((d) => d.avg_response_time_ms !== undefined);
  const avgResponse = withTiming.length
    ? Math.round(
        withTiming.reduce((sum, d) => sum + (d.avg_response_time_ms || 0), 0) / withTiming.length
      )
    : 0;

  return (
    <AppShell
      title="Data sources"
      sub="Connectors and coverage"
      sourceCount={datasources.length}
      actions={
        <>
          <div className="srch">
            <Icon d={ICONS.search} strokeWidth={2.2} />
            <input
              placeholder="Search sources…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button className="btn btn-p" onClick={() => setShowAddModal(true)}>
            <Icon d={ICONS.plus} strokeWidth={2.4} />
            Add source
          </button>
        </>
      }
    >
      <div className="ph">
        <div>
          <span className="eye">Sources</span>
          <h3>Every system, one layer</h3>
          <p>
            OmniMind sits on top of what you already run. Each source connects through its own
            governed interface, and every answer carries the source it came from.
          </p>
        </div>
      </div>

      {/* Coverage */}
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: 12 }}>
        <Kpi
          label="Sources live"
          value={activeCount}
          color="var(--green)"
          icon={ICONS.plug}
          sub={`${datasources.length} configured`}
        />
        <Kpi label="Queries run" value={totalQueries.toLocaleString('en-US')} color="var(--blue)" icon={ICONS.db} />
        <Kpi
          label="Avg response"
          value={avgResponse}
          unit=" ms"
          color="var(--purple)"
          icon={ICONS.clock}
        />
        <Kpi
          label="Needing attention"
          value={errorCount}
          color={errorCount ? 'var(--red)' : 'var(--muted-2)'}
          icon={ICONS.warn}
          sub={errorCount ? 'connection errors' : 'all healthy'}
        />
      </div>

      {/* Files are a source too */}
      <DocumentUpload />

      {/* Controls */}
      <div
        className="card"
        style={{ padding: '11px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <div className="tabs">
          {[{ id: 'all', label: 'All types' }, ...presentTypes].map((f) => (
            <button
              key={f.id}
              className={`tab${filterType === f.id ? ' on' : ''}`}
              onClick={() => setFilterType(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="eye" style={{ margin: 0 }}>
            Sort
          </span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="inp"
            style={{ width: 'auto', padding: '6px 10px' }}
          >
            <option value="name">Name</option>
            <option value="type">Type</option>
            <option value="created">Date created</option>
            <option value="lastSync">Last sync</option>
          </select>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="card">
          <div className="empty">
            <h5>Loading sources…</h5>
            <p>Reading connector status and freshness.</p>
          </div>
        </div>
      ) : sortedDatasources.length === 0 ? (
        <div className="card">
          <div className="empty">
            <h5>{datasources.length === 0 ? 'No sources connected yet' : 'No results'}</h5>
            <p style={{ maxWidth: '48ch', margin: '0 auto 14px' }}>
              {datasources.length === 0
                ? 'Connect a system and OmniMind will start answering from it — read-only, with your own permissions.'
                : 'Try a different search or filter.'}
            </p>
            {datasources.length === 0 && (
              <button className="btn btn-p" onClick={() => setShowAddModal(true)}>
                <Icon d={ICONS.plus} strokeWidth={2.4} />
                Connect your first source
              </button>
            )}
          </div>
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gap: 12,
            gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
          }}
        >
          {sortedDatasources.map((datasource) => (
            <DatasourceCard
              key={datasource.id}
              datasource={datasource}
              onView={() => setSelectedDatasource(datasource)}
              onDelete={() => handleDeleteDatasource(datasource.id)}
            />
          ))}
        </div>
      )}

      <div className="note" style={{ marginTop: 14 }}>
        <b>Read-only by default.</b> OmniMind cannot post, approve or send anything through a
        connector. Write access needs an explicit grant and a human confirmation.
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddDatasourceModal
          onClose={() => setShowAddModal(false)}
          onSuccess={handleDatasourceCreated}
        />
      )}

      {selectedDatasource && (
        <DatasourceDetailModal
          datasource={selectedDatasource}
          onClose={() => setSelectedDatasource(null)}
          onUpdate={handleDatasourceUpdated}
          onDelete={() => {
            handleDeleteDatasource(selectedDatasource.id);
            setSelectedDatasource(null);
          }}
        />
      )}
    </AppShell>
  );
};
